import { formatObservationValue, isResource, resolveId } from '@medplum/core'
import type {
  CodeableConcept,
  Device,
  Encounter,
  Group,
  Location,
  Observation,
  ObservationComponent,
  Patient,
  Practitioner,
  Quantity,
  Reference,
} from '@medplum/fhirtypes'
import { isEmpty } from 'lodash-es'
import { match } from 'ts-pattern'
import { z } from 'zod'
import {
  codeableConcept,
  codeFromCodeableConcept,
  displayFromCodeableConcept,
  hasCodeableConcept,
} from '#src/codeableConcept.ts'
import {
  ELATION_ID_SYSTEM,
  ELATION_SANDBOX_ID_SYSTEM,
  OBSERVATION,
  OBSERVATION_INTERPRETATION_SYSTEM,
} from '#src/codeSystems.ts'
import { HEALTH_GORILLA_META_SOURCE } from '#src/constants.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import { ExtendedClientApplication } from '#src/models/clientApplication.ts'
import {
  BLOOD_PRESSURE_CODING,
  BODY_HEIGHT_CODING,
  BODY_MASS_INDEX_CODING,
  BODY_TEMPERATURE_CODING,
  BODY_WEIGHT_CODING,
  HEAD_CIRCUMFERENCE_CODING,
  HEART_RATE_CODING,
  OXYGEN_SATURATION_CODING,
  PAIN_SEVERITY_CODING,
  RESPIRATORY_RATE_CODING,
} from '#src/models/loinc.ts'
import type { QueryTypes } from '#src/search.ts'
import { generateUUID, isOfType, now } from '#src/utils.ts'

export class ExtendedObservation extends ExtendedResource<Observation> {
  static readonly RESOURCE_TYPE = 'Observation'

  static isInstance(value: unknown): value is Observation {
    return isResource(value) && isOfType(value, 'Observation')
  }

  static isBodyWeight(observation: Observation): boolean {
    return hasCodeableConcept(observation.code, BODY_WEIGHT_CODING)
  }

  static fromValueCodeableConcept({
    valueCodeableConcept,
    code,
    participant,
    practitioner,
    note,
  }: {
    valueCodeableConcept: CodeableConcept
    code: CodeableConcept
    practitioner: Reference<Practitioner>
    participant: Reference<Patient> | undefined
    note?: string
  }): ExtendedObservation {
    const observation: Omit<Observation, 'resourceType'> = {
      code,
      subject: participant,
      effectiveDateTime: now(),
      performer: [practitioner],
      status: 'final',
      valueCodeableConcept,
    }
    if (note?.trim()) {
      observation.note = [
        {
          text: note.trim(),
          id: generateUUID(),
          authorReference: practitioner,
          time: now(),
        },
      ]
    }
    return new ExtendedObservation(observation)
  }

  static fromValueQuantity({
    value,
    unit,
    code,
    participant,
    practitioner,
    note,
  }: {
    value: number
    unit: string
    code: CodeableConcept
    practitioner: Reference<Practitioner>
    participant: Reference<Patient> | undefined
    note?: string
  }): ExtendedObservation {
    const observation: Omit<Observation, 'resourceType'> = {
      code,
      subject: participant,
      effectiveDateTime: now(),
      performer: [practitioner],
      status: 'final',
      valueQuantity: {
        value,
        unit,
        system: 'http://unitsofmeasure.org',
        code: unit,
      },
    }
    if (note?.trim()) {
      observation.note = [
        {
          text: note.trim(),
          id: generateUUID(),
          authorReference: practitioner,
          time: now(),
        },
      ]
    }
    return new ExtendedObservation(observation)
  }

  static fromValueString({
    value,
    code,
    participant,
    practitioner,
    note,
  }: {
    value: string
    code: CodeableConcept
    practitioner: Reference<Practitioner>
    participant: Reference<Patient> | undefined
    note?: string
  }): ExtendedObservation {
    const observation: Omit<Observation, 'resourceType'> = {
      code,
      subject: participant,
      effectiveDateTime: now(),
      performer: [practitioner],
      status: 'final',
      valueString: value.trim(),
    }
    if (note?.trim()) {
      observation.note = [
        {
          text: note.trim(),
          id: generateUUID(),
          authorReference: practitioner,
          time: now(),
        },
      ]
    }
    return new ExtendedObservation(observation)
  }

  static fromComponent({
    component,
    code,
    participant,
    practitioner,
    note,
  }: {
    component: ObservationComponent[]
    code: CodeableConcept
    practitioner: Reference<Practitioner>
    participant: Reference<Patient> | undefined
    note?: string
  }): ExtendedObservation {
    const observation: Omit<Observation, 'resourceType'> = {
      code,
      subject: participant,
      effectiveDateTime: now(),
      performer: [practitioner],
      status: 'final',
      component,
    }
    if (note?.trim()) {
      observation.note = [
        {
          text: note.trim(),
          id: generateUUID(),
          authorReference: practitioner,
          time: now(),
        },
      ]
    }
    return new ExtendedObservation(observation)
  }

  static buildFromString({
    subject,
    code,
    value,
    effectiveDateTime,
  }: {
    subject: Reference<Patient | Group | Device | Location>
    code: z.infer<typeof ExtendedObservation.codeSchema>
    value: string
    effectiveDateTime?: string
  }): ExtendedObservation {
    const observation: Omit<Observation, 'resourceType'> = {
      subject,
      code: codeableConcept({
        code,
        system: OBSERVATION,
      }),
      status: 'preliminary',
      effectiveDateTime: effectiveDateTime ?? now(),
    }
    /* Omit rather than write "". FHIR has no empty string, so Medplum
     * rejects the whole resource with `Invalid additional property
     * "valueString"` — a body-less note has no value element at all. */
    if (value) {
      observation.valueString = value
    }
    return new ExtendedObservation(observation)
  }

  static searchQuery({
    subject,
    code,
    date,
  }: {
    subject?: Reference<Patient | Group | Device | Location>
    code?: z.infer<typeof ExtendedObservation.codeSchema>
    date?: string
  }): QueryTypes {
    const query: QueryTypes = { subject, code }
    if (date) {
      query.date = date
    }
    query._sort = '-_lastUpdated'
    query._count = 1000
    query['status:not'] = 'entered-in-error'
    return query
  }

  // Sorted by `date` (effectiveDateTime) rather than `_lastUpdated`, because
  // the caller wants the note belonging to the latest earlier day, not the one
  // edited most recently — an old note reopened today would otherwise win.
  static queryMostRecentBefore({
    subject,
    code,
    before,
  }: {
    subject: Reference<Patient | Group | Device | Location>
    code: z.infer<typeof ExtendedObservation.codeSchema>
    before: string
  }): QueryTypes {
    return {
      subject,
      code,
      date: `lt${before}`,
      _sort: '-date',
      _count: 1,
      'status:not': 'entered-in-error',
    }
  }

  // Every observation the participant's Observations page lists, so the
  // facesheet card's "See all" count matches the page's row count.
  static participantObservationsQuery(
    subject: Reference<Patient> | string
  ): QueryTypes {
    return { subject, 'status:not': 'entered-in-error' }
  }

  static queryByIds(ids: string[]): QueryTypes {
    return {
      _id: ids,
      _count: ids.length,
      _sort: '-_lastUpdated',
      'status:not': 'entered-in-error',
    }
  }

  static labSearchQuery({
    subject,
    codes,
  }: {
    subject: Reference<Patient | Group | Device | Location>
    codes: CodeableConcept[]
  }): QueryTypes {
    return {
      subject,
      code: codes
        .map((c) => `http://loinc.org|${codeFromCodeableConcept(c)}`)
        .join(','),
      _sort: '-date',
      _count: 1000,
      'status:not': 'entered-in-error',
    }
  }

  static getDefaultAnswerOptions(
    scaleType: 'Ord' | 'Nom',
    componentName?: string
  ): { code: string; display: string }[] {
    if (scaleType === 'Ord') {
      if (
        componentName?.toLowerCase().includes('presence') ||
        componentName?.toLowerCase().includes('detected')
      ) {
        return [
          { code: 'POS', display: 'Positive' },
          { code: 'NEG', display: 'Negative' },
        ]
      }
      return [
        { code: 'YES', display: 'Yes' },
        { code: 'NO', display: 'No' },
      ]
    }
    if (scaleType === 'Nom') {
      return [{ code: 'UNK', display: 'Unknown' }]
    }
    throw new Error('Invalid scale type')
  }

  get status(): Observation['status'] {
    return this.resource.status
  }

  set status(value: Observation['status']) {
    this.resource.status = value
  }

  markAsEnteredInError(): void {
    this.resource.status = 'entered-in-error'
  }

  get isEnteredInError(): boolean {
    return this.resource.status === 'entered-in-error'
  }

  // Not yet signed or finalized. An allow-list, so a status added later reads
  // as not preliminary.
  get isPreliminary(): boolean {
    return this.resource.status === 'preliminary'
  }

  static codeSchema = z.enum(['day-center-day-note'])

  get code(): string | undefined {
    return codeFromCodeableConcept(this.resource.code)
  }

  get display(): string | undefined {
    return (
      displayFromCodeableConcept(this.resource.code) ?? this.resource.code.text
    )
  }

  get codeableConcept(): CodeableConcept {
    return this.resource.code
  }

  get valueDisplay(): string | undefined {
    return formatObservationValue(this.resource)
  }

  get valueQuantity(): number | undefined {
    return this.resource.valueQuantity?.value
  }

  set valueQuantity(value: Quantity | undefined) {
    this.resource.valueQuantity = value
  }

  updateValueQuantityValue(value: number | undefined): void {
    invariant(this.resource.valueQuantity, 'valueQuantity is required')
    this.resource.valueQuantity = {
      ...this.resource.valueQuantity,
      value,
    }
  }

  get isNormal(): boolean {
    const code = codeFromCodeableConcept(
      this.resource.interpretation,
      OBSERVATION_INTERPRETATION_SYSTEM
    )
    if (!code) {
      return true
    }

    return code === 'N'
  }

  get valueUnit(): string | undefined {
    return this.resource.valueQuantity?.unit
  }

  get valueString(): string | undefined {
    return this.resource.valueString
  }

  set valueString(value: string | undefined) {
    this.resource.valueString = value
  }

  get valueCodeableConcept(): CodeableConcept | undefined {
    return this.resource.valueCodeableConcept
  }

  set valueCodeableConcept(value: CodeableConcept | undefined) {
    this.resource.valueCodeableConcept = value
  }

  get effectiveDateTime(): string | undefined {
    return this.resource.effectiveDateTime
  }

  set effectiveDateTime(value: string | undefined) {
    this.resource.effectiveDateTime = value
  }

  get issued(): string | undefined {
    return this.resource.issued
  }

  get encounter(): Reference<Encounter> | undefined {
    return this.resource.encounter
  }

  set encounter(value: Reference<Encounter> | undefined) {
    this.resource.encounter = value
  }

  get subject(): Reference<Patient | Group | Device | Location> | undefined {
    return this.resource.subject
  }

  get performerIds(): string[] | undefined {
    return this.resource.performer?.map((p) => resolveId(p) ?? '')
  }

  get performer(): string | undefined {
    return this.resource.performer?.map((p) => p.display).join(', ')
  }

  get performerPractitioner(): Reference<Practitioner> | undefined {
    const performer = this.resource.performer?.[0]
    return isOfType(performer, 'Practitioner') ? performer : undefined
  }

  set performerPractitioner(performer: Reference<Practitioner>) {
    this.resource.performer = [performer]
  }

  get performingPractitioner(): Reference<Practitioner> | undefined {
    return this.resource.performer?.find((p) => isOfType(p, 'Practitioner'))
  }

  // Where the observation came from: the system that synced it, else the
  // clinician who recorded it, else Seen Health for app and bot writes.
  get sourceLabel(): string | undefined {
    if (this.syncedFrom) {
      return this.syncedFrom
    }
    const author = this.resource.meta?.author
    return (
      this.sourcePractitioner?.display ??
      (author &&
      (isOfType(author, 'ClientApplication') || isOfType(author, 'Bot'))
        ? ExtendedClientApplication.companyName
        : undefined)
    )
  }

  // The clinician who recorded the observation, when no external system
  // synced it: the performer, else the author, else who the write was on
  // behalf of. The first with a saved display wins, so it matches sourceLabel.
  get sourcePractitioner(): Reference<Practitioner> | undefined {
    if (this.syncedFrom) {
      return undefined
    }
    const { author, onBehalfOf } = this.resource.meta ?? {}
    const candidates = [this.performingPractitioner, author, onBehalfOf].filter(
      (reference): reference is Reference<Practitioner> =>
        reference !== undefined && isOfType(reference, 'Practitioner')
    )
    return (
      candidates.find((reference) => reference.display !== undefined) ??
      candidates[0]
    )
  }

  private get syncedFrom(): 'Elation' | 'Health Gorilla' | undefined {
    const identifierSystems =
      this.resource.identifier?.map((identifier) => identifier.system) ?? []
    if (
      identifierSystems.some(
        (system) =>
          system === ELATION_ID_SYSTEM || system === ELATION_SANDBOX_ID_SYSTEM
      )
    ) {
      return 'Elation'
    }
    if (
      this.resource.meta?.source === HEALTH_GORILLA_META_SOURCE ||
      identifierSystems.includes(HEALTH_GORILLA_META_SOURCE) ||
      this.resource.meta?.author?.display?.includes('health-gorilla')
    ) {
      return 'Health Gorilla'
    }
    return undefined
  }

  get interpretationCodes(): string[] | undefined {
    return this.resource.interpretation
      ?.map((c) => codeFromCodeableConcept(c))
      .filter((c) => c !== undefined)
  }

  get interpretationDisplay(): string | undefined {
    return displayFromCodeableConcept(
      this.resource.interpretation,
      OBSERVATION_INTERPRETATION_SYSTEM
    )
  }

  /*
    The following Observation#code's will come back from Quest. These are not normal Observations, but rather a Messages from Quest.
    This appears to be the way that HealthGorilla maps the messages back through FHIR. Sometimes there are duplicative messages, so we can dedupe.
    http://loinc.org 8264-4 MESSAGE:
    text = QUESTION/PROBLEM
    text = CONTAINER TYPE:
    text = FINAL RESOLUTION
  */
  get isMessageType(): boolean {
    if (
      hasCodeableConcept(
        this.resource.code,
        codeableConcept({
          system: 'http://loinc.org',
          code: '8264-4',
        })
      ) ||
      hasCodeableConcept(
        this.resource.code,
        codeableConcept({
          system: 'http://loinc.org',
          code: '19145-2',
        })
      )
    ) {
      return true
    }

    // If it's only a text code, it's a message type
    if (
      Object.keys(this.resource.code).length === 1 &&
      'text' in this.resource.code
    ) {
      return true
    }

    const text = this.resource.code.text?.trim()
    if (!text) {
      return false
    }

    return (
      text.startsWith('QUESTION/PROBLEM') ||
      text.startsWith('CONTAINER TYPE') ||
      text.startsWith('FINAL RESOLUTION')
    )
  }

  get noteSummary(): string | undefined {
    return this.resource.note?.map((n) => n.text).join(', ')
  }

  get noteFirstText(): string | undefined {
    return this.resource.note?.[0]?.text
  }

  setNoteFirst(
    text: string | undefined,
    author: Reference<Practitioner>
  ): void {
    const trimmedText = text?.trim()
    const result = [
      ...(trimmedText
        ? [
            {
              text: trimmedText,
              id: this.resource.note?.[0]?.id ?? generateUUID(),
              authorString: author.display,
              authorReference: author,
              time: now(),
            },
          ]
        : []),
      ...(this.resource.note ?? []).slice(1),
    ]
    this.resource.note = isEmpty(result) ? undefined : result
  }

  get hasMember(): Observation['hasMember'] {
    return this.resource.hasMember
  }

  get derivedFrom(): Observation['derivedFrom'] {
    return this.resource.derivedFrom
  }

  get referenceRange(): Observation['referenceRange'] {
    return this.resource.referenceRange
  }

  get component(): Observation['component'] {
    return this.resource.component
  }

  set component(value: Observation['component']) {
    this.resource.component = value
  }

  /**
   * Returns a formatted display string for component-based observations.
   * For example, "126/60 mmHg" for blood pressure.
   */
  get componentDisplay(): string | undefined {
    if (!this.resource.component?.length) {
      return undefined
    }

    const componentValues = this.resource.component
      .map((comp) => {
        if (comp.valueQuantity?.value !== undefined) {
          return String(comp.valueQuantity.value)
        }
        if (comp.valueString) {
          return comp.valueString
        }
        if (comp.valueCodeableConcept) {
          return displayFromCodeableConcept(comp.valueCodeableConcept)
        }
        return undefined
      })
      .filter((v) => v !== undefined)

    if (componentValues.length === 0) {
      return undefined
    }

    // Get the unit from the first component that has one
    const unit = this.resource.component.find(
      (comp) => comp.valueQuantity?.unit
    )?.valueQuantity?.unit

    return componentValues.join('/') + (unit ? ` ${unit}` : '')
  }

  /**
   * Returns true if this observation has components instead of a direct value
   */
  get hasComponents(): boolean {
    return !!this.resource.component?.length
  }

  protected getExportFields() {
    return {
      id: this.id,
      status: this.status,
      code: this.code,
      display: this.display,
      valueDisplay: this.valueDisplay,
      valueQuantity: this.valueQuantity,
      valueUnit: this.valueUnit,
      valueString: this.valueString,
      valueCodeableConcept: codeFromCodeableConcept(this.valueCodeableConcept),
      effectiveDateTime: this.effectiveDateTime,
      issued: this.issued,
      patientId: resolveId(this.subject),
      performerIds: this.performerIds,
      interpretation: this.interpretationCodes,
      note: this.noteSummary,
    }
  }

  static getNickname(code: string, display: string): string {
    return match<string | undefined>(code)
      .with(codeFromCodeableConcept(BLOOD_PRESSURE_CODING), () => 'BP')
      .with(codeFromCodeableConcept(HEART_RATE_CODING), () => 'HR')
      .with(codeFromCodeableConcept(BODY_TEMPERATURE_CODING), () => 'Temp')
      .with(codeFromCodeableConcept(RESPIRATORY_RATE_CODING), () => 'RR')
      .with(codeFromCodeableConcept(BODY_WEIGHT_CODING), () => 'Wt')
      .with(codeFromCodeableConcept(BODY_HEIGHT_CODING), () => 'Ht')
      .with(codeFromCodeableConcept(BODY_MASS_INDEX_CODING), () => 'BMI')
      .with(codeFromCodeableConcept(OXYGEN_SATURATION_CODING), () => 'O₂')
      .with(codeFromCodeableConcept(HEAD_CIRCUMFERENCE_CODING), () => 'HC')
      .with(codeFromCodeableConcept(PAIN_SEVERITY_CODING), () => 'Pain')
      .otherwise(() => display)
  }
}
